import { createContext, PropsWithChildren, useEffect } from 'react'

import { ResolveWorkFileConflictRequestDto } from '../../_generated/api/workfile-import'
import DataContextValue from './DataContextValue'
import { useApiContext } from './ApiContext'
import { useUserContext } from './UserContext'
import { useToastContext } from './ToastContext'
import { useAction } from '../../hooks/action'
import { useProvideContext } from '../../hooks/context'
import { usePaginatedTable } from '../../hooks/pagination'
import TableControl from '../../models/TableControl'
import Workfile from '../../models/Workfile'
import WorkfileConflict from '../../models/WorkfileConflict'
import { mapReadResponse, mapReadTableResponse, ok, throwIfResponseNotOk } from '../../tools/api'
import { downloadBlob, downloadText } from '../../tools/dom/download'

export interface WorkfileContextValue extends DataContextValue {
    workfiles: TableControl<Workfile>
    workfilesLoading: boolean
    refreshWorkfiles: () => Promise<TableControl<Workfile> | undefined>
    uploadWorkfile: (files: File[]) => Promise<void>
    downloadWorkfile: (workfileId: string) => void
    resolveWorkfileConflict: (
        workfileId: string,
        conflictId: string,
        resolution: ResolveWorkFileConflictRequestDto
    ) => Promise<WorkfileConflict | undefined>
}

const WorkfileContext = createContext<WorkfileContextValue | undefined>(undefined)

export default function ({ children }: PropsWithChildren) {
    const { workfileApi } = useApiContext()
    const { selectedOrganization, lazySelectedOrganizationId } = useUserContext()
    const Toast = useToastContext()

    const [workfiles, workfilesLoading, workfilesError, refreshWorkfiles] = usePaginatedTable(
        async (table: TableControl<Workfile>) => {
            const orgId = selectedOrganization?.id
            if (!orgId) return null
            const response = await workfileApi.getWorkFiles({
                tenantId: orgId,
                sortBy: table.sortBy,
                ascending: table.ascending,
                skip: table.skip,
                take: table.take,
            })
            return mapReadTableResponse(response, table, Workfile)
        },
        [selectedOrganization?.id]
    )

    const [uploadingWorkfiles, uploadWorkfilesError, uploadWorkfile] = useAction(
        async (files: File[]) => {
            const orgId = selectedOrganization?.id
            if (!orgId) return
            for (const file of files) {
                const response = await workfileApi.uploadWorkFile({ tenantId: orgId, file })
                if (ok(response)) Toast.success('upload', 'workfile')
                else Toast.danger('Upload failed', `File "${file.name}" could not be uploaded.`)
            }
            await refreshWorkfiles()
        }
    )

    const [downloadLoading, downloadError, downloadWorkfile] = useAction(
        async (workfileId?: string) => {
            if (!workfileId) return
            const response = await workfileApi.downloadWorkFile({
                id: workfileId,
                tenantId: lazySelectedOrganizationId,
            })
            throwIfResponseNotOk(response)
            const content = response.data as any
            const filename = `${workfileId}_workfile_export`
            const extension = '.jsonl'
            if (response.data instanceof Blob) downloadBlob(filename, response.data, extension)
            else downloadText(filename, content.toString(), extension)
        }
    )

    const [resolvingWorkfileConflict, resolveWorkfileConflictError, resolveWorkfileConflict] =
        useAction(
            async (
                workfileId: string,
                conflictId: string,
                resolution: ResolveWorkFileConflictRequestDto
            ) => {
                const orgId = selectedOrganization?.id
                if (!orgId) return
                const response = await workfileApi.resolveWorkFileConflict({
                    id: workfileId,
                    conflictId,
                    tenantId: orgId,
                    resolveWorkFileConflictRequestDto: resolution,
                })
                const conflict = mapReadResponse(response, WorkfileConflict)
                await refreshWorkfiles()
                return conflict
            }
        )

    const error =
        workfilesError || uploadWorkfilesError || resolveWorkfileConflictError || downloadError
    const loading = workfilesLoading || uploadingWorkfiles || resolvingWorkfileConflict

    useEffect(() => {
        if (error) Toast.error(error, 'Workfile error')
    }, [error])

    const value: WorkfileContextValue = {
        loading,
        error,

        workfiles,
        workfilesLoading,
        refreshWorkfiles,
        uploadWorkfile,
        downloadWorkfile,
        resolveWorkfileConflict,
    }

    return <WorkfileContext.Provider value={value}>{children}</WorkfileContext.Provider>
}

export const useWorkfileContext = () => useProvideContext(WorkfileContext, 'Workfile')
